import { ChangeEvent, ReactNode } from "react";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ProductFacePriceUsagePricebook } from "../../../types/product.type";
import ConfirmModal from "../../ConfirmModal";
import {
  DRAFT_LABEL,
  GROUP_RADIUS,
  formatDiscountPercent,
} from "../PriceBook/constants";
import { PricePair } from "../PriceBook/Details/PriceChangeList";
import { FacePriceImpactDialogState } from "./useFacePriceImpact";

/** Fixed so the % and the two price pairs line up from row to row. */
const DISCOUNT_WIDTH = 56;
const PRICE_PAIR_WIDTH = 140;

const DESCRIPTION =
  "สมุดราคาเหล่านี้ใช้ราคาหน้าซองเดิมเป็นราคาต่อหน่วยของสินค้านี้ เลือกสมุดที่จะเปลี่ยนเป็นราคาหน้าซองใหม่ (คิดส่วนลดเดิมของแต่ละสมุด) สมุดที่ไม่เลือกคงราคาเดิม";

interface FacePriceImpactDialogProps extends FacePriceImpactDialogState {
  saving: boolean;
  onConfirm: () => void;
}

/**
 * Asked when a product's ราคาหน้าซอง changes: the pricebooks still priced at the old face price,
 * each with what it bills at now and at the new face price, incl and ex VAT. The ticked ones are
 * saved with the product as `apply_face_price_to`.
 */
export default function FacePriceImpactDialog({
  usage,
  selectedCodes,
  saving,
  onToggle,
  onToggleAll,
  onClose,
  onConfirm,
}: FacePriceImpactDialogProps) {
  const pricebooks = usage?.pricebooks ?? [];
  const isEverySelected = selectedCodes.length === pricebooks.length;
  const isSomeSelected = selectedCodes.length > 0 && !isEverySelected;

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleToggleAll = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onToggleAll(checked);
  };

  return (
    <ConfirmModal
      open={usage !== null}
      title="อัปเดตสมุดราคาตามราคาหน้าซอง"
      description={DESCRIPTION}
      cancelLabel="กลับไปแก้"
      confirmLabel="บันทึก"
      loading={saving}
      onClose={handleClose}
      onConfirm={onConfirm}
    >
      <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 2 }}>
        <Typography variant="body2">ราคาหน้าซอง</Typography>
        <PricePair
          before={usage?.old_face_price ?? null}
          after={usage?.new_face_price ?? null}
        />
        <Typography variant="body2">บาท</Typography>
      </Stack>
      <Stack
        sx={{
          mt: 2,
          border: (theme) => `1px solid ${theme.palette.draft.main}`,
          borderRadius: GROUP_RADIUS,
        }}
      >
        <BookLine
          select={
            <Checkbox
              size="small"
              checked={isEverySelected}
              indeterminate={isSomeSelected}
              disabled={saving}
              onChange={handleToggleAll}
              slotProps={{ input: { "aria-label": "เลือกทุกสมุด" } }}
            />
          }
          name="สมุดราคา"
          discount="ส่วนลด"
          inclVat="รวม VAT"
          exVat="ก่อน VAT"
          isHeader
        />
        {pricebooks.map((book) => (
          <BookRow
            key={book.pricebook_uuid}
            book={book}
            isSelected={selectedCodes.includes(book.pricebook_code)}
            disabled={saving}
            onToggle={onToggle}
          />
        ))}
      </Stack>
    </ConfirmModal>
  );
}

interface BookRowProps {
  book: ProductFacePriceUsagePricebook;
  isSelected: boolean;
  disabled: boolean;
  onToggle: (pricebookCode: string) => void;
}

function BookRow({ book, isSelected, disabled, onToggle }: BookRowProps) {
  const bookLabel = book.pricebook_name
    ? `${book.pricebook_code} ${book.pricebook_name}`
    : book.pricebook_code;

  const handleToggle = () => onToggle(book.pricebook_code);

  return (
    <BookLine
      select={
        <Checkbox
          size="small"
          checked={isSelected}
          disabled={disabled}
          onChange={handleToggle}
          slotProps={{ input: { "aria-label": bookLabel } }}
        />
      }
      name={bookLabel}
      tag={
        book.is_draft && (
          <Chip label={DRAFT_LABEL} size="small" variant="outlined" />
        )
      }
      discount={formatDiscountPercent(book.applied_discount_percent)}
      inclVat={
        <PricePair
          before={book.old_price_incl_vat}
          after={book.new_price_incl_vat}
        />
      }
      exVat={
        <PricePair before={book.old_price_ex_vat} after={book.new_price_ex_vat} />
      }
    />
  );
}

interface BookLineProps {
  select: ReactNode;
  name: string;
  tag?: ReactNode;
  discount: string;
  inclVat: ReactNode;
  exVat: ReactNode;
  isHeader?: boolean;
}

function BookLine({
  select,
  name,
  tag,
  discount,
  inclVat,
  exVat,
  isHeader = false,
}: BookLineProps) {
  const textColor = isHeader ? "text.secondary" : "text.primary";

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        pr: 1.5,
        py: 0.5,
        borderBottom: (theme) => `1px solid ${theme.palette.draft.main}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      {select}
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ flexGrow: 1, minWidth: 0 }}
      >
        <Typography variant="body2" color={textColor} noWrap>
          {name}
        </Typography>
        {tag}
      </Stack>
      <Typography
        variant="body2"
        color={textColor}
        align="right"
        sx={{ width: DISCOUNT_WIDTH, flexShrink: 0 }}
      >
        {discount}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        color={textColor}
        align="right"
        sx={{ width: PRICE_PAIR_WIDTH, flexShrink: 0 }}
      >
        {inclVat}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        color={textColor}
        align="right"
        sx={{ width: PRICE_PAIR_WIDTH, flexShrink: 0 }}
      >
        {exVat}
      </Typography>
    </Stack>
  );
}
